import { invokeEdgeFunction, EdgeFunctionError } from './invokeEdgeFunction';
import type { CreatePack } from './taskCreateIntake';
import {
  formatScheduleBoardContext,
  parseScheduleHorizon,
  type ScheduleBoardContext,
  type ScheduleHorizon,
} from './taskScheduleContext';
import type { TaskValueResource } from './networkingTasks';

export type TaskCorpusPurpose = 'create_intake' | 'force_rescore';

type CorpusRefs = {
  articles?: Array<{ title?: string; url?: string; similarity?: number }>;
  transcripts?: Array<{
    id?: string;
    title?: string;
    engine_url?: string;
    excerpt?: string;
    similarity?: number;
  }>;
  people?: Array<{
    id?: number;
    name?: string;
    engine_url?: string;
    similarity?: number;
    icp?: string | null;
  }>;
  references?: Array<{ title?: string; url?: string | null; similarity?: number }>;
};

export type TaskCorpusLookup = {
  reply: string;
  resources: TaskValueResource[];
  /** Clean bullets for internal notes — not dumped raw to chat. */
  digest: string;
  /** Clarifying questions — shown as an open-questions list. */
  questions: string[];
  /** Board card title from the model (create intake). */
  name?: string;
  /** Tight task description from the model (create intake). */
  description?: string;
  score?: number | null;
  scoreWhy?: string;
  dueLocal?: string | null;
  priority?: string;
};

function buildPrompt(args: {
  purpose: TaskCorpusPurpose;
  title?: string;
  notes?: string;
  seed?: string;
  value?: string;
  outcome?: string;
  priority?: string;
  epicTitle?: string | null;
}): string {
  const body = [
    args.title ? `Title: ${args.title}` : '',
    args.seed ? `Work: ${args.seed}` : '',
    args.value ? `Value: ${args.value}` : '',
    args.outcome ? `Done: ${args.outcome}` : '',
    args.priority ? `If we don’t: ${args.priority}` : '',
    args.notes ? `Notes: ${args.notes}` : '',
    args.epicTitle ? `Current epic: ${args.epicTitle}` : '',
  ]
    .filter(Boolean)
    .join('\n')
    .slice(0, 6000);

  if (args.purpose === 'force_rescore') {
    return (
      `CEO task force-rescore. Using the corpus, extract evidence for economic ($), goals, and network value.\n` +
      `Reply in plain text only (no JSON). 5 short bullets max: value signals; supporting people/meetings; contradictions; suggested score 1–5 + why; anything missing.\n\n` +
      body
    );
  }

  return (
    `CEO task create intake. Using the corpus, finalize the ticket and help place this work.\n` +
    `Reply in PLAIN TEXT only — never JSON, never code fences.\n\n` +
    `NAME: <board card / tab title — max 6 words, specific, no filler>\n` +
    `DESCRIPTION: <exactly 3 short lines: (1) why it matters (2) Done: checkable outcome (3) If not: failure/cost>\n` +
    `PRIORITY: <failure / cost if we don't — one short sentence>\n` +
    `HORIZON: <short | near | long>\n` +
    `DUE: <YYYY-MM-DD — default today; later only if sequence/horizon needs it; never past>\n` +
    `DUE_WHY: <one short line>\n` +
    `SCORE: <1-5 or none — prefer 4–5 when money/impact is clear>\n` +
    `SCORE_WHY: <one short line, or none>\n\n` +
    `DIGEST:\n` +
    `- <one short bullet: value signals>\n` +
    `- <one short bullet: related people/meetings or none>\n` +
    `- <one short bullet: duplicate risk or none>\n` +
    `- <one short bullet: epic/theme if any>\n\n` +
    `QUESTIONS:\n` +
    `- <clarifying question ending with ?>\n` +
    `- <optional second question ending with ?>\n` +
    `If nothing to ask, write exactly: QUESTIONS: none\n\n` +
    body
  );
}

function stripCodeFences(text: string): string {
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  return fence?.[1]?.trim() || text.trim();
}

function tryParseJsonBlob(raw: string): Record<string, unknown> | null {
  const text = stripCodeFences(raw);
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start < 0 || end <= start) return null;
  try {
    const parsed = JSON.parse(text.slice(start, end + 1)) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    /* not JSON */
  }
  return null;
}

function bulletsFromUnknown(value: unknown): string[] {
  if (value == null) return [];
  if (typeof value === 'string') {
    const t = value.trim();
    if (!t || /^none$/i.test(t)) return [];
    return [t];
  }
  if (Array.isArray(value)) {
    return value
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter((v) => v.length >= 3 && !/^none$/i.test(v));
  }
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => {
        const s = typeof v === 'string' ? v.trim() : '';
        if (!s || /^none$/i.test(s)) return '';
        return s;
      })
      .filter(Boolean);
  }
  return [];
}

/** Normalize model reply (text or accidental JSON) into clean digest + questions. */
export function normalizeCorpusReply(reply: string): { digest: string; questions: string[] } {
  const text = stripCodeFences(reply);
  const json = tryParseJsonBlob(text);

  if (json) {
    const digestObj = json.DIGEST ?? json.digest ?? json;
    const digestParts =
      typeof digestObj === 'object' && digestObj && !Array.isArray(digestObj)
        ? bulletsFromUnknown(digestObj)
        : bulletsFromUnknown(digestObj);
    const qRaw = json.QUESTIONS ?? json.questions;
    let questions = bulletsFromUnknown(qRaw).map((q) => (q.endsWith('?') ? q : `${q}?`));
    questions = questions.filter((q) => q.length >= 12).slice(0, 3);
    const digest = digestParts
      .map((b) => (b.startsWith('-') ? b : `- ${b}`))
      .join('\n')
      .slice(0, 800);
    return { digest, questions };
  }

  return {
    digest: digestFromPlainText(text),
    questions: extractAiQuestionsFromPlainText(text),
  };
}

/** Pull clarifying questions from plain-text create-intake reply. */
export function extractAiQuestions(reply: string): string[] {
  return normalizeCorpusReply(reply).questions;
}

function extractAiQuestionsFromPlainText(reply: string): string[] {
  const text = reply.trim();
  if (!text) return [];
  if (/\bQUESTIONS:\s*none\b/i.test(text)) return [];

  const section = text.match(/QUESTIONS:\s*([\s\S]*?)(?=\n\s*[A-Z]{3,}:\s*|$)/i)?.[1] ?? '';
  const qs: string[] = [];
  const source = section || text;
  for (const line of source.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || /^QUESTIONS:/i.test(trimmed) || /^none$/i.test(trimmed)) continue;
    if (/^DIGEST:/i.test(trimmed)) continue;
    const m = trimmed.match(
      /^(?:[-*]|\d+[.)])\s*(.+)$|^(?:Q(?:uestion)?\s*\d*\s*[:.)-]|Pushback\s*[:.)-])\s*(.+)$/i,
    );
    const q = (m?.[1] || m?.[2] || '').trim();
    if (q.length >= 12 && q.includes('?')) qs.push(q.slice(0, 300));
    else if (
      q.length >= 20 &&
      /^(who|what|when|where|why|how|which|is|are|do|does|can|should)\b/i.test(q)
    ) {
      qs.push((q.endsWith('?') ? q : `${q}?`).slice(0, 300));
    }
  }
  if (qs.length === 0) {
    const asks = text.match(/[^.!?\n]+\?/g);
    if (asks) {
      for (const a of asks.slice(-2)) {
        const q = a.trim();
        if (q.length >= 12) qs.push(q.slice(0, 300));
      }
    }
  }
  return [...new Set(qs)].slice(0, 3);
}

function digestFromPlainText(reply: string): string {
  const dig = reply.match(/DIGEST:\s*([\s\S]*?)(?=\n\s*QUESTIONS:|$)/i)?.[1]?.trim();
  const body = (dig || reply)
    .replace(/^\s*\{[\s\S]*$/m, '')
    .trim();
  const lines = body
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l && !/^QUESTIONS:/i.test(l) && !l.startsWith('{'));
  const bullets = lines
    .map((l) => l.replace(/^[-*]\s*/, ''))
    .filter((l) => l.length >= 8)
    .slice(0, 6)
    .map((l) => `- ${l}`);
  const out = bullets.join('\n') || body.slice(0, 400);
  return out.length > 600 ? `${out.slice(0, 597).trim()}…` : out;
}

/** Format open questions for chat — no JSON, no “corpus” jargon. */
export function formatOpenQuestionsList(questions: string[]): string {
  if (questions.length === 0) return '';
  const lines = questions.map((q, i) => `${i + 1}. ${q}`);
  return `Open questions:\n${lines.join('\n')}`;
}

function refsToResources(refs: CorpusRefs | undefined): TaskValueResource[] {
  if (!refs) return [];
  const out: TaskValueResource[] = [];
  for (const p of refs.people ?? []) {
    if (!p.name?.trim()) continue;
    out.push({
      kind: 'connection',
      label: p.name.trim(),
      id: p.id != null ? String(p.id) : undefined,
      url: p.engine_url,
      note: p.icp ? `ICP: ${p.icp}` : 'Related person',
    });
  }
  for (const t of refs.transcripts ?? []) {
    if (!t.title?.trim()) continue;
    out.push({
      kind: 'transcript',
      label: t.title.trim(),
      id: t.id,
      url: t.engine_url,
      note: t.excerpt?.slice(0, 160) || 'Related meeting',
    });
  }
  for (const a of refs.articles ?? []) {
    if (!a.title?.trim()) continue;
    out.push({
      kind: 'article',
      label: a.title.trim(),
      url: a.url,
      note: 'Related article',
    });
  }
  for (const r of refs.references ?? []) {
    if (!r.title?.trim()) continue;
    out.push({
      kind: 'other',
      label: r.title.trim(),
      url: r.url ?? undefined,
      note: 'Saved reference',
    });
  }
  return out.slice(0, 12);
}

/**
 * Same corpus edge as MCP `engine_ask_corpus` / Writing chat.
 * Fail soft — callers continue if this throws/returns null.
 */
export async function lookupTaskCorpus(args: {
  purpose: TaskCorpusPurpose;
  title?: string;
  notes?: string;
  seed?: string;
  value?: string;
  outcome?: string;
  priority?: string;
  epicTitle?: string | null;
}): Promise<TaskCorpusLookup | null> {
  const prompt = buildPrompt(args);
  if (prompt.length < 40) return null;

  try {
    const data = await invokeEdgeFunction<{
      reply?: string;
      error?: string;
      refs?: CorpusRefs;
    }>('site-content-prompt', {
      prompt,
      sources: ['articles', 'transcripts', 'people', 'references'],
      history: [],
    });

    if (data?.error) throw new Error(data.error);
    const reply = data?.reply?.trim();
    if (!reply) return null;

    const resources = refsToResources(data.refs);
    const { digest, questions } =
      args.purpose === 'create_intake'
        ? normalizeCorpusReply(reply)
        : { digest: digestFromPlainText(reply), questions: [] as string[] };

    let name = '';
    let description = '';
    let score: number | null | undefined;
    let scoreWhy: string | undefined;
    let dueLocal: string | null | undefined;
    let priority: string | undefined;
    if (args.purpose === 'create_intake') {
      const review = parseCreateTicketReply(reply);
      name = review.name || '';
      description = review.description || '';
      score = review.score;
      scoreWhy = review.scoreWhy;
      dueLocal = review.dueLocal;
      priority = review.priority;
    }

    return {
      reply,
      resources,
      digest,
      questions,
      name: name || undefined,
      description: description || undefined,
      score,
      scoreWhy,
      dueLocal,
      priority,
    };
  } catch (e: unknown) {
    if (e instanceof EdgeFunctionError || e instanceof Error) {
      console.warn('[taskChatCorpus]', e.message);
    }
    return null;
  }
}

const CREATE_LABELS =
  'NAME|DESCRIPTION|WORK|VALUE|DONE|PRIORITY|FAILURE|SCORE|SCORE_WHY|DUE|DUE_WHY|HORIZON|EPIC|DIGEST|QUESTIONS';

function extractLabeledFromReply(text: string, label: string, allowMultiline = false): string {
  if (allowMultiline) {
    const re = new RegExp(
      `(?:^|\\n)\\s*${label}\\s*[:\\-–—]\\s*([\\s\\S]+?)(?=\\n\\s*(?:${CREATE_LABELS})\\s*[:\\-]|$)`,
      'i',
    );
    const m = text.match(re);
    return m?.[1]?.trim().replace(/\n{3,}/g, '\n\n').slice(0, 520) ?? '';
  }
  const re = new RegExp(
    `(?:^|\\n)\\s*${label}\\s*[:\\-–—]\\s*([^\\n]+(?:\\n(?!\\s*(?:${CREATE_LABELS})\\s*[:\\-])[^\\n]+)*)`,
    'i',
  );
  const m = text.match(re);
  return m?.[1]?.trim().replace(/\n+/g, ' ').slice(0, 280) ?? '';
}

/** Parse AI DUE into datetime-local string (local calendar — no UTC date-only shift). */
export function parseDueFromAiReply(raw: string): string | null {
  const t = raw.trim();
  if (!t || /^none$/i.test(t) || /^n\/?a$/i.test(t) || /^default$/i.test(t) || /^keep$/i.test(t)) {
    return null;
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  const toLocal = (d: Date) => {
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Date-only: parse as local Y-M-D (avoid `new Date('YYYY-MM-DD')` UTC midnight)
  const dateOnly = t.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  if (dateOnly) {
    const y = Number(dateOnly[1]);
    const m = Number(dateOnly[2]);
    const d = Number(dateOnly[3]);
    const timeM = t.match(/T(\d{1,2}):(\d{2})/);
    const hour = timeM ? Number(timeM[1]) : 9;
    const minute = timeM ? Number(timeM[2]) : 0;
    return toLocal(new Date(y, m - 1, d, hour, minute, 0, 0));
  }

  const now = new Date();
  const lower = t.toLowerCase();
  if (/\btoday\b/.test(lower)) {
    const d = new Date(now);
    d.setHours(9, 0, 0, 0);
    return toLocal(d);
  }
  if (/\btomorrow\b/.test(lower)) {
    const d = new Date(now);
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return toLocal(d);
  }
  const inDays = lower.match(/\bin\s+(\d+)\s+days?\b/);
  if (inDays) {
    const d = new Date(now);
    d.setDate(d.getDate() + Number(inDays[1]));
    d.setHours(9, 0, 0, 0);
    return toLocal(d);
  }
  if (/\bthis week\b|\bend of week\b|\bby friday\b|\bfriday\b/.test(lower)) {
    const d = new Date(now);
    const day = d.getDay();
    const add = day <= 5 ? 5 - day : 6;
    d.setDate(d.getDate() + (add === 0 ? 7 : add));
    d.setHours(9, 0, 0, 0);
    return toLocal(d);
  }
  if (/\bnext week\b/.test(lower)) {
    const d = new Date(now);
    d.setDate(d.getDate() + 7);
    d.setHours(9, 0, 0, 0);
    return toLocal(d);
  }
  return null;
}

export type CreateTicketReview = Partial<CreatePack> & {
  name?: string;
  description?: string;
  score?: number | null;
  scoreWhy?: string;
  dueLocal?: string | null;
  dueWhy?: string;
  horizon?: ScheduleHorizon | null;
  epicHint?: string;
  digest?: string;
};

/** @deprecated Use CreateTicketReview */
export type InferredCreateWording = CreateTicketReview;

const SCORE_GUIDANCE =
  `Scoring (1–5): If NAME/DESCRIPTION/VALUE show clear money, revenue, client delivery, or strategic impact, ` +
  `prefer SCORE 4 or 5 with SCORE_WHY one short line. If impact is weak/unclear, use 1–3. ` +
  `Only SCORE: none when there is truly no basis to judge. Prefer scoring when value is inferable — do not wait for the human.`;

const SCHEDULE_GUIDANCE =
  `Scheduling rules (strict):\n` +
  `1) DEFAULT DUE = today (the Today date in SCHEDULE BOARD CONTEXT).\n` +
  `2) NEVER schedule before today. NEVER copy a past epic target_date as DUE.\n` +
  `3) Only pick a later date when HORIZON is near/long or the work clearly sequences after other open tasks — then prefer the next empty day in that band.\n` +
  `4) HORIZON: short (today / 0–2d) | near (3–10d) | long (11–30d or toward a FUTURE epic target).\n` +
  `5) If epic target is PAST, ignore it; schedule from today forward.\n` +
  `6) DUE must be concrete YYYY-MM-DD. DUE_WHY: one short line.`;

function parseCreateTicketReply(replyRaw: string): CreateTicketReview {
  const reply = stripCodeFences(replyRaw.trim());
  if (tryParseJsonBlob(reply)) {
    const norm = normalizeCorpusReply(reply);
    return { digest: norm.digest };
  }

  const name = extractLabeledFromReply(reply, 'NAME') || extractLabeledFromReply(reply, 'WORK');
  const description = extractLabeledFromReply(reply, 'DESCRIPTION', true);
  const value = extractLabeledFromReply(reply, 'VALUE');
  const done = extractLabeledFromReply(reply, 'DONE');
  const priority =
    extractLabeledFromReply(reply, 'PRIORITY') || extractLabeledFromReply(reply, 'FAILURE');
  const epicRaw = extractLabeledFromReply(reply, 'EPIC');
  const scoreRaw = extractLabeledFromReply(reply, 'SCORE');
  const scoreWhy = extractLabeledFromReply(reply, 'SCORE_WHY');
  const dueRaw = extractLabeledFromReply(reply, 'DUE');
  let score: number | null = null;
  if (scoreRaw && !/^none$/i.test(scoreRaw)) {
    const sm = scoreRaw.match(/([1-5])/);
    if (sm) score = Number(sm[1]);
  }
  const epicHint =
    epicRaw && !/^none$/i.test(epicRaw) ? epicRaw.replace(/^ops[-\s]?inbox$/i, 'ops-inbox') : '';

  const dueWhy = extractLabeledFromReply(reply, 'DUE_WHY');
  const horizonRaw = extractLabeledFromReply(reply, 'HORIZON');
  const horizon = horizonRaw ? parseScheduleHorizon(horizonRaw) : null;

  return {
    seed: name || '',
    name: name || undefined,
    description: description || undefined,
    value: value || undefined,
    outcome: done || undefined,
    priority: priority || undefined,
    score,
    scoreWhy: scoreWhy || undefined,
    dueLocal: dueRaw ? parseDueFromAiReply(dueRaw) : null,
    dueWhy: dueWhy || undefined,
    horizon,
    epicHint: epicHint || undefined,
  };
}

/**
 * Ask model to fill planning fields + finished card wording from a dump.
 * UI applies fields as returned — does not re-invent locally.
 */
export async function inferCreatePackViaCorpus(
  dump: string,
  scheduleContext?: ScheduleBoardContext | null,
): Promise<CreateTicketReview | null> {
  const text = dump.trim();
  if (text.length < 40) return null;
  const scheduleBlock = scheduleContext
    ? `\n\nSCHEDULE BOARD CONTEXT:\n${formatScheduleBoardContext(scheduleContext)}`
    : '';

  try {
    const data = await invokeEdgeFunction<{
      reply?: string;
      error?: string;
    }>('site-content-prompt', {
      prompt:
        `You are creating a CEO task ticket. Own as much as possible — the UI will apply your fields as-is.\n` +
        `Reply in PLAIN TEXT only (no JSON). Exact labels:\n` +
        `NAME: <board card / tab title — max 6 words, specific, no filler>\n` +
        `DESCRIPTION: <exactly 3 short lines: (1) why it matters (2) Done: checkable outcome (3) If not: cost of delay / failure>\n` +
        `VALUE: <one short sentence — money / impact / why>\n` +
        `DONE: <one short sentence — checkable outcome>\n` +
        `PRIORITY: <one short sentence — failure / cost if we don't>\n` +
        `HORIZON: <short | near | long>\n` +
        `DUE: <YYYY-MM-DD — default today; later only if near/long horizon or sequence; never past>\n` +
        `DUE_WHY: <one short line — horizon + board load + epic window>\n` +
        `SCORE: <1-5 or none>\n` +
        `SCORE_WHY: <one short line, or none>\n` +
        `EPIC: <initiative name hint, ops-inbox, or none>\n\n` +
        `${SCORE_GUIDANCE}\n` +
        `${SCHEDULE_GUIDANCE}\n\n` +
        `Dump:\n${text.slice(0, 3500)}${scheduleBlock}`,
      sources: ['articles', 'transcripts', 'people', 'references'],
      history: [],
    });

    if (data?.error || !data?.reply?.trim()) return null;
    return parseCreateTicketReply(data.reply);
  } catch (e: unknown) {
    if (e instanceof EdgeFunctionError || e instanceof Error) {
      console.warn('[inferCreatePackViaCorpus]', e.message);
    }
    return null;
  }
}

/**
 * Full ticket review — rewrite name/description/priority/due/score from current pack + new info.
 * Call whenever the human adds information during create.
 */
export async function reviewCreateTicketViaCorpus(args: {
  pack: CreatePack;
  name?: string;
  description?: string;
  score?: number | null;
  dueLocal?: string;
  newInfo?: string;
  /** Open board + epic window — required for smart DUE. */
  scheduleContext?: ScheduleBoardContext | null;
}): Promise<CreateTicketReview | null> {
  const scheduleBlock = args.scheduleContext
    ? formatScheduleBoardContext(args.scheduleContext)
    : '';
  const body = [
    args.name ? `Current name: ${args.name}` : '',
    args.description ? `Current description:\n${args.description}` : '',
    args.pack.seed ? `Work: ${args.pack.seed}` : '',
    args.pack.value ? `Value: ${args.pack.value}` : '',
    args.pack.outcome ? `Done: ${args.pack.outcome}` : '',
    args.pack.priority ? `If we don’t / failure: ${args.pack.priority}` : '',
    args.score != null ? `Current score: ${args.score}/5` : '',
    args.dueLocal ? `Current due: ${args.dueLocal}` : '',
    args.newInfo ? `New information from human:\n${args.newInfo}` : '',
    scheduleBlock ? `SCHEDULE BOARD CONTEXT:\n${scheduleBlock}` : '',
  ]
    .filter(Boolean)
    .join('\n')
    .slice(0, 8000);
  if (body.length < 20) return null;

  try {
    const data = await invokeEdgeFunction<{
      reply?: string;
      error?: string;
    }>('site-content-prompt', {
      prompt:
        `Review and update this CEO task ticket with the latest information. ` +
        `You may tweak NAME, DESCRIPTION, PRIORITY (failure if not done), DUE, and SCORE. ` +
        `Prefer completing the ticket yourself — minimize further human questions.\n` +
        `Reply in PLAIN TEXT only (no JSON). Exact labels:\n` +
        `NAME: <board card / tab title — max 6 words, specific>\n` +
        `DESCRIPTION: <exactly 3 short lines: (1) why it matters (2) Done: … (3) If not: failure/cost>\n` +
        `VALUE: <one short sentence>\n` +
        `DONE: <one short sentence>\n` +
        `PRIORITY: <failure / cost if we don't — one short sentence>\n` +
        `HORIZON: <short | near | long>\n` +
        `DUE: <YYYY-MM-DD concrete, or keep>\n` +
        `DUE_WHY: <one short line>\n` +
        `SCORE: <1-5 or none>\n` +
        `SCORE_WHY: <one short line, or none>\n` +
        `EPIC: <hint, ops-inbox, or none>\n\n` +
        `${SCORE_GUIDANCE}\n` +
        `${SCHEDULE_GUIDANCE}\n` +
        `If DUE should stay unchanged, write DUE: keep\n\n` +
        `Ticket state:\n${body}`,
      sources: ['articles', 'transcripts', 'people', 'references'],
      history: [],
    });

    if (data?.error || !data?.reply?.trim()) return null;
    const review = parseCreateTicketReply(data.reply);
    const dueRaw = extractLabeledFromReply(stripCodeFences(data.reply), 'DUE');
    if (/^keep$/i.test(dueRaw.trim())) {
      review.dueLocal = args.dueLocal || null;
    }
    return review;
  } catch (e: unknown) {
    if (e instanceof EdgeFunctionError || e instanceof Error) {
      console.warn('[reviewCreateTicketViaCorpus]', e.message);
    }
    return null;
  }
}

/**
 * Finished wording (+ score/due when inferable) from a planning pack.
 */
export async function proposeTaskWordingViaCorpus(
  pack: CreatePack,
): Promise<CreateTicketReview | null> {
  return reviewCreateTicketViaCorpus({ pack });
}
